import React, { useState } from 'react';
import { IioTable } from '../../components/IioTable';
import { useProject } from '../../hooks/useProject';
import { useExports, useRenameExport, isExportInProgress } from '../../hooks/useExports';
import { hasPermission } from '../../types/project';
import { ExportInfoModal } from './ExportInfoModal';
import { ExportModal } from './ExportModal';
import type { Export } from '../../hooks/useExports';
import { FilterInput } from '../../components/FilterInput';
import { useCurrentUserEmail } from '../../hooks/useCurrentUserEmail';
import { JobProgressBar } from '../../components/JobProgressBar';
import { HelpLink } from '../../components/HelpLink';
import { EditableText } from '../../components/EditableText';

interface Props {
  projectId: string;
}

export function ExportsTab({ projectId }: Props) {
  const { data: project } = useProject(projectId);
  const canManageExports = project ? hasPermission(project, 'MANAGE_EXPORTS') : false;
  const { data: exports } = useExports(projectId);
  const [nameFilter, setNameFilter] = useState('');
  const [selectedExport, setSelectedExport] = useState<Export | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const rename = useRenameExport(projectId);

  const currentUserEmail = useCurrentUserEmail();

  const allExports = exports ?? [];
  const filtered = allExports.filter(e =>
    e.name.toLowerCase().includes(nameFilter.toLowerCase())
  );

  const helpLink = (
    <HelpLink href="https://folio-accessid.zendesk.com/hc/en-us/sections/202895606-Exports-and-Productions" />
  );

  return (
    <>
      <h2>
        <i className="fas fa-share" aria-hidden="true" />{' '}
        {project?.name} Exports
      </h2>

      {allExports.length === 0 ? (
        <div className="alert alert-block">
          <h4>No exports found</h4>
          <br />
          {canManageExports && (
            <p>
              <button
                className="btn btn-primary"
                onClick={() => setShowExportModal(true)}
              >
                Add my first export <i className="fas fa-plus-circle" aria-hidden="true" />
              </button>
              {' '}
              {helpLink}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-md-8">
              <div className="btn-toolbar">
                {canManageExports && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowExportModal(true)}
                  >
                    Add Export <i className="fas fa-plus-circle" aria-hidden="true" />
                  </button>
                )}
                {helpLink}
                <FilterInput value={nameFilter} onChange={setNameFilter} />
              </div>
            </div>
            <div className="col-md-4" style={{ textAlign: 'right' }}>
              <p className="lead">You have {allExports.length} Exports</p>
            </div>
          </div>

          <IioTable condensed>
            <thead>
              <tr>
                <th>Name</th>
                <th>Output Types</th>
                <th>Creator</th>
                <th>StartBates</th>
                <th>EndBates</th>
                <th>Created At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(exp => {
                const isComplete = !!exp.path && !exp.hadExportException;
                const isFailed = exp.hadExportException || exp.status === 'failure';
                const isInProgress = isExportInProgress(exp);
                const endBatesIsNull = exp.endBates?.includes('null');

                return (
                  <tr key={exp.id}>
                    <td>
                      <EditableText
                        value={exp.name}
                        onSave={name => rename.mutateAsync({ ...exp, name })}
                        onValueClick={() => setSelectedExport(exp)}
                        existingValues={allExports.filter(e => e.id !== exp.id).map(e => e.name)}
                        disabled={!canManageExports}
                      />
                    </td>
                    <td>
                      {(exp.outputTypes ?? []).map(t => (
                        <span key={t} className="label label-default" style={{ marginRight: 2 }}>{t}</span>
                      ))}
                    </td>
                    <td>
                      {exp.account.email === currentUserEmail && (
                        <i className="fas fa-user" title="Me" aria-hidden="true" />
                      )}{' '}
                      {exp.account.fullName}{' '}
                      {exp.account.email && (
                        <small className="text-muted">{exp.account.email}</small>
                      )}
                    </td>
                    <td>{exp.startBates}</td>
                    <td>
                      {endBatesIsNull
                        ? <i className="fas fa-circle-notch fa-spin fa-lg fa-fw" style={{ color: 'lightgrey' }} aria-hidden="true" />
                        : exp.endBates}
                    </td>
                    <td>{exp.createdAtString}</td>
                    <td style={{ width: 110 }}>
                      {isComplete && (
                        <>
                          <span className="label label-success">Complete</span>
                          {' '}
                          {exp.directDownloadPath && (
                            <a
                              className="btn btn-xs btn-primary"
                              href={exp.directDownloadPath}
                              title="Download"
                              download
                            >
                              <i className="fas fa-download" aria-hidden="true" />
                            </a>
                          )}
                        </>
                      )}
                      {isInProgress && (
                        <JobProgressBar percent={exp.job?.percentageComplete} />
                      )}
                      {isFailed && (
                        <span className="label label-danger">Failed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </IioTable>
        </>
      )}

      {selectedExport && project && (
        <ExportInfoModal
          projectId={projectId}
          exportObj={selectedExport}
          onClose={() => setSelectedExport(null)}
        />
      )}

      {showExportModal && project && (
        <ExportModal
          project={project}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </>
  );
}
